import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Clock3 } from "lucide-react";
import { Button } from "./Button";
import { formatDateTime } from "../lib/format";
import { enablePushNotifications, getPushConfig } from "../lib/push-notifications";

function autoCloseAt(plannedEnd: string) {
  return new Date(new Date(plannedEnd).getTime() + 60 * 60 * 1000);
}

export function SessionLifecycleSurface() {
  const { pathname } = useLocation();
  const match = pathname.match(/^\/(?:sessions|live)\/(\d+)$/);
  const id = match?.[1] ?? null;
  const liveMode = pathname.startsWith("/live/");
  const queryClient = useQueryClient();
  const [now, setNow] = useState(Date.now());
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const sessionQuery = useQuery({
    queryKey: ["carpintel_session", id],
    queryFn: () => fetch(`/app-api/sessions/${id}`).then((r) => r.json()),
    enabled: Boolean(id),
    refetchInterval: id ? 60_000 : false,
  });

  const pushQuery = useQuery({
    queryKey: ["catchcompass_push_config"],
    queryFn: getPushConfig,
    enabled: Boolean(id),
    staleTime: 60_000,
  });

  const session = sessionQuery.data;
  const closeAt = useMemo(
    () => (session?.planned_end_at ? autoCloseAt(session.planned_end_at) : null),
    [session?.planned_end_at],
  );
  const minutes = closeAt ? Math.max(0, Math.ceil((closeAt.getTime() - now) / 60_000)) : null;
  const warning = minutes !== null && minutes <= 15;

  if (!id || !session || session.end_at) return null;

  async function setPlannedEnd(date: Date) {
    setWorking(true);
    setMessage("");
    try {
      const response = await fetch(`/app-api/sessions/${id}/planned-end`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ planned_end_at: date.toISOString() }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Could not update planned finish");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["carpintel_session", id] }),
        queryClient.invalidateQueries({ queryKey: ["carpintel_sessions"] }),
      ]);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not update planned finish");
    } finally {
      setWorking(false);
    }
  }

  async function extendOneHour() {
    const base = session.planned_end_at
      ? Math.max(new Date(session.planned_end_at).getTime(), Date.now())
      : Date.now();
    await setPlannedEnd(new Date(base + 60 * 60 * 1000));
  }

  async function enableAlerts() {
    setWorking(true);
    setMessage("");
    try {
      await enablePushNotifications();
      await pushQuery.refetch();
      setMessage("Session alerts are enabled on this device.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not enable alerts");
    } finally {
      setWorking(false);
    }
  }

  if (liveMode) {
    return (
      <div className="mx-auto max-w-[760px] px-4 pt-3">
        <div className={`rounded-xl border px-3 py-2.5 ${warning ? "border-warning bg-warning-weak" : "border-border bg-raised"}`}>
          <div className="flex flex-wrap items-center justify-between gap-2.5">
            <div className="min-w-0 flex-1">
              <p className={`flex items-center gap-2 text-sm font-semibold ${warning ? "text-warning" : "text-primary"}`}>
                <Clock3 className="size-4 shrink-0" />
                {warning
                  ? `Auto-close in about ${minutes} minute${minutes === 1 ? "" : "s"}`
                  : session.planned_end_at
                    ? "Finish protected"
                    : "Planned finish not set"}
              </p>
              <p className="mt-0.5 truncate text-xs text-secondary">
                {session.planned_end_at && closeAt
                  ? `Planned ${formatDateTime(session.planned_end_at)} · auto-close ${formatDateTime(closeAt.toISOString())}`
                  : "Add a planned finish so CatchCompass can close the session if you forget."}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {session.planned_end_at ? (
                <Button className="min-h-10" size="sm" variant={warning ? "secondary" : "tertiary"} onClick={extendOneHour} isLoading={working}>
                  Extend +1h
                </Button>
              ) : (
                <Button className="min-h-10" size="sm" onClick={() => setPlannedEnd(new Date(Date.now() + 8 * 60 * 60 * 1000))} isLoading={working}>
                  Set finish +8h
                </Button>
              )}
              {pushQuery.data?.supported && !pushQuery.data?.subscribed ? (
                <Button className="min-h-10" size="sm" variant="tertiary" onClick={enableAlerts} isLoading={working}>
                  <Bell className="size-4" /> Alerts
                </Button>
              ) : null}
            </div>
          </div>
          {message ? <p className="mt-2 text-xs text-secondary" role="status">{message}</p> : null}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1200px] px-4 pt-3">
      <div className={`rounded-xl border p-3 ${warning ? "border-warning bg-warning-weak" : "border-border bg-raised"}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className={`flex items-center gap-2 text-sm font-semibold ${warning ? "text-warning" : "text-primary"}`}>
              <Clock3 className="size-4" />
              {warning
                ? `Session auto-closes in about ${minutes} minute${minutes === 1 ? "" : "s"}`
                : session.planned_end_at
                  ? "Session finish protection"
                  : "Set a planned finish"}
            </p>
            <p className="mt-1 text-xs leading-5 text-secondary">
              {session.planned_end_at && closeAt
                ? `Planned finish ${formatDateTime(session.planned_end_at)}. If you forget to end it, CatchCompass closes it at ${formatDateTime(closeAt.toISOString())}.`
                : "Older live sessions do not have a planned finish. Add one so CatchCompass can close the session if you forget."}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {session.planned_end_at ? (
              <Button size="sm" variant={warning ? "secondary" : "tertiary"} onClick={extendOneHour} isLoading={working}>
                Extend +1h
              </Button>
            ) : (
              <Button size="sm" onClick={() => setPlannedEnd(new Date(Date.now() + 8 * 60 * 60 * 1000))} isLoading={working}>
                Set finish +8h
              </Button>
            )}
            {pushQuery.data?.supported && !pushQuery.data?.subscribed ? (
              <Button size="sm" variant="tertiary" onClick={enableAlerts} isLoading={working}>
                <Bell className="size-4" /> Enable session alerts
              </Button>
            ) : null}
          </div>
        </div>
        {message ? <p className="mt-2 text-xs text-secondary" role="status">{message}</p> : null}
      </div>
    </div>
  );
}
